import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router';
import '@qm/brand/tokens.css';
import '@qm/ui/styles.css';
import '@qm/ui/kit.css';
import './shell/shell.css';
import './shell/screens.css';
import './shell/redesign.css';
import './shell/motion.css';
import './shell/typography.css';
import './shell/concept.css';
import { surfaceFromHost } from '@qm/tenancy/surface';
import { routesForSurface } from './routes';
import { AppProvider } from './lib/app-context';
import { storedLocale } from './lib/prefs';
import { startRum } from './lib/rum';
import { applyCachedUiText } from './lib/ui-text';

const router = createBrowserRouter(routesForSurface(surfaceFromHost(window.location.host)));
let initialLocale: 'en' | 'ar' = 'en';
try { initialLocale = storedLocale(window.localStorage) ?? (navigator.language.startsWith('ar') ? 'ar' : 'en'); } catch { /* storage blocked */ }

// Staff-edited wording (Portal text CMS): start with the copy kept from the last visit; the provider refreshes it in the background.
applyCachedUiText(initialLocale);

// PWA: the service worker caches only the public shell (see public/sw.js, QM-D19-003).
if ('serviceWorker' in navigator && window.location.protocol === 'https:') {
  void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => undefined);
}
startRum();

// /design-system: the @qm/ui kit reference (standalone, synthetic data, no BFF calls). Loaded on demand only.
const DesignSystem = lazy(() => import('./design/DesignSystem').then((m) => ({ default: m.DesignSystem })));
const isDesignSystem = window.location.pathname.replace(/\/+$/, '') === '/design-system';

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {isDesignSystem ? (
      <Suspense fallback={null}><DesignSystem /></Suspense>
    ) : (
      <AppProvider initialLocale={initialLocale}>
        <RouterProvider router={router} />
      </AppProvider>
    )}
  </StrictMode>,
);
